'use client';

import { useMemo, useState, useEffect, useReducer, useCallback } from 'react';
import {
  defaultProjectData,
  connectionDefaultData,
  STORAGE_KEY_ACCESS_TOKEN,
} from 'src/config';
import { authService } from 'src/services/auth.service';
import axiosInstance from 'src/services/axios';
import { handleErrorResponse } from 'src/services/utils';
import { ActionMapType } from 'src/types';

import { GlobalContext } from './global-context';
import {
  IUser,
  GlobalStateType,
  ProjectDataType,
  ShowDialogsType,
  GlobalIpSubnetType,
  GlobalSettingsType,
  ExportSettingsType,
} from './types';

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  SET_GLOBAL_LOADING = 'SET_GLOBAL_LOADING',
  SET_SHOW_DIALOGS = 'SET_SHOW_DIALOGS',
  SET_USER = 'SET_USER',
  SET_USE_CUSTOM_VLAN = 'SET_USE_CUSTOM_VLAN',
  SET_GLOBAL_IP_SUBNET = 'SET_GLOBAL_IP_SUBNET',
  SET_LAST_CHANGED = 'SET_LAST_CHANGED',
  SET_GLOBAL_SETTINGS = 'SET_GLOBAL_SETTINGS',
  SET_PROJECT_DATA = 'SET_PROJECT_DATA',
  SET_EXPORT_SETTINGS = 'SET_EXPORT_SETTINGS',
  SET_DISABLE_FLOW = 'SET_DISABLE_FLOW',
}

type Payload = {
  [Types.INITIAL]: {
    globalLoading: boolean;
    user?: IUser;
    lastChanged: string;
  };
  [Types.SET_GLOBAL_LOADING]: {
    loading: boolean;
  };
  [Types.SET_USER]: {
    user: IUser | undefined;
  };
  [Types.SET_USE_CUSTOM_VLAN]: {
    value: boolean;
  };
  [Types.SET_GLOBAL_IP_SUBNET]: {
    value: GlobalIpSubnetType | undefined;
  };
  [Types.SET_LAST_CHANGED]: {
    value: string;
  };
  [Types.SET_GLOBAL_SETTINGS]: {
    value: GlobalSettingsType | undefined;
  };
  [Types.SET_PROJECT_DATA]: {
    value: Partial<ProjectDataType>;
  };
  [Types.SET_SHOW_DIALOGS]: {
    value: Partial<ShowDialogsType>;
  };
  [Types.SET_EXPORT_SETTINGS]: {
    value: Partial<ExportSettingsType>;
  };
  [Types.SET_DISABLE_FLOW]: {
    value: boolean;
  };
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: GlobalStateType = {
  lastChanged: '',
  globalLoading: true,
  showDialogs: {
    vlan: false,
    package: false,
    setting: false,
    downloadFile: false,
    globalSettings: false,
    projectData: false,
    exportSettings: false,
    exportFrame: false,
    exportDocumentation: false,
  },
  user: undefined,
  useCustomVlan: false,
  globalIpSubnet: undefined,
  globalSettings: undefined,
  projectData: defaultProjectData,
  disableFlow: false,
};

const reducer = (state: GlobalStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      ...state,
      globalLoading: action.payload.globalLoading,
      user: action.payload.user,
      lastChanged: action.payload.lastChanged,
    };
  }
  if (action.type === Types.SET_GLOBAL_LOADING) {
    return {
      ...state,
      globalLoading: action.payload.loading,
    };
  }
  if (action.type === Types.SET_SHOW_DIALOGS) {
    const newShowDialogs = { ...state.showDialogs, ...action.payload.value };
    // check some dialogs are open and disable flow
    if (Object.values(newShowDialogs).some((value) => value)) {
      return {
        ...state,
        showDialogs: newShowDialogs,
        disableFlow: true,
      };
    }
    return {
      ...state,
      showDialogs: newShowDialogs,
      disableFlow: false,
    };
  }

  if (action.type === Types.SET_USER) {
    return {
      ...state,
      user: action.payload.user,
    };
  }

  if (action.type === Types.SET_USE_CUSTOM_VLAN) {
    return {
      ...state,
      useCustomVlan: action.payload.value,
    };
  }

  if (action.type === Types.SET_GLOBAL_IP_SUBNET) {
    return {
      ...state,
      globalIpSubnet: action.payload.value,
    };
  }

  if (action.type === Types.SET_LAST_CHANGED) {
    return {
      ...state,
      lastChanged: action.payload.value,
    };
  }

  if (action.type === Types.SET_GLOBAL_SETTINGS) {
    return {
      ...state,
      globalSettings: action.payload.value,
    };
  }

  if (action.type === Types.SET_PROJECT_DATA) {
    return {
      ...state,
      projectData: { ...state.projectData, ...action.payload.value },
    };
  }

  if (action.type === Types.SET_EXPORT_SETTINGS) {
    return {
      ...state,
      projectData: {
        ...state.projectData,
        exportSettings: {
          ...state.projectData.exportSettings,
          ...action.payload.value,
        },
      },
    };
  }

  if (action.type === Types.SET_DISABLE_FLOW) {
    return {
      ...state,
      disableFlow: action.payload.value,
    };
  }

  return state;
};

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export function GlobalProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [vlans, setVlans] = useState(() => [...connectionDefaultData]);

  const initialize = useCallback(async () => {
    // Skip initialization in test environment to avoid unnecessary side effects
    if (import.meta.env.VITEST) {
      dispatch({
        type: Types.INITIAL,
        payload: {
          globalLoading: false,
          user: undefined,
          lastChanged: new Date().toISOString(),
        },
      });
      return;
    }

    // validate user is expired and password is correct
    const token = localStorage.getItem(STORAGE_KEY_ACCESS_TOKEN);
    if (token) {
      const getCurrentUser = await authService.getCurrentUser();
      dispatch({
        type: Types.INITIAL,
        payload: {
          user: getCurrentUser.user,
          globalLoading: false,
          lastChanged: new Date().toISOString(),
        },
      });
    } else {
      dispatch({
        type: Types.INITIAL,
        payload: {
          globalLoading: false,
          user: undefined,
          lastChanged: new Date().toISOString(),
        },
      });
    }
  }, []);

  const setGlobalLoading = useCallback((loading: boolean) => {
    dispatch({
      type: Types.SET_GLOBAL_LOADING,
      payload: { loading },
    });
  }, []);

  const setShowDialogs = useCallback(
    (payload: Partial<ShowDialogsType>) => {
      dispatch({
        type: Types.SET_SHOW_DIALOGS,
        payload: { value: { ...state.showDialogs, ...payload } },
      });
    },
    [state.showDialogs],
  );

  const login = useCallback(
    async ({
      email,
      password,
    }: {
      email: string;
      password: string;
    }): Promise<{ success: boolean; message: string }> => {
      try {
        const loginInfo = await authService.login({ email, password });
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${loginInfo.session_token}`;
        localStorage.setItem(STORAGE_KEY_ACCESS_TOKEN, loginInfo.session_token);
        dispatch({
          type: Types.SET_USER,
          payload: { user: loginInfo.user },
        });
        return { success: true, message: '' };
      } catch (error) {
        return { success: false, message: handleErrorResponse(error) };
      }
    },
    [],
  );

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY_ACCESS_TOKEN);
    dispatch({
      type: Types.SET_USER,
      payload: { user: undefined },
    });
  }, []);

  const setUseCustomVlan = useCallback((payload: boolean) => {
    dispatch({
      type: Types.SET_USE_CUSTOM_VLAN,
      payload: { value: payload },
    });
  }, []);

  const setGlobalIpSubnet = useCallback(
    (payload: GlobalIpSubnetType | undefined) => {
      dispatch({
        type: Types.SET_GLOBAL_IP_SUBNET,
        payload: { value: payload },
      });
    },
    [],
  );

  const setUser = useCallback((payload: IUser) => {
    dispatch({
      type: Types.SET_USER,
      payload: { user: payload },
    });
  }, []);

  const setLastChanged = useCallback((payload: string) => {
    dispatch({
      type: Types.SET_LAST_CHANGED,
      payload: { value: payload },
    });
  }, []);

  const setGlobalSettings = useCallback(
    (payload: GlobalSettingsType | undefined) => {
      dispatch({
        type: Types.SET_GLOBAL_SETTINGS,
        payload: { value: payload },
      });
    },
    [],
  );

  const setProjectData = useCallback((payload: Partial<ProjectDataType>) => {
    dispatch({
      type: Types.SET_PROJECT_DATA,
      payload: { value: payload },
    });
  }, []);

  const setExportSettings = useCallback(
    (payload: Partial<ExportSettingsType>) => {
      dispatch({
        type: Types.SET_EXPORT_SETTINGS,
        payload: { value: payload },
      });
    },
    [],
  );

  const setDisableFlow = useCallback((payload: boolean) => {
    dispatch({
      type: Types.SET_DISABLE_FLOW,
      payload: { value: payload },
    });
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // ----------------------------------------------------------------------

  const memoizedValue = useMemo(
    () => ({
      globalLoading: state.globalLoading,
      setGlobalLoading,
      showDialogs: state.showDialogs,
      setShowDialogs,
      disableFlow: state.disableFlow,
      setDisableFlow,
      vlans,
      setVlans,
      user: state.user,
      setUser,
      login,
      logout,
      useCustomVlan: state.useCustomVlan || false,
      setUseCustomVlan,
      globalIpSubnet: state.globalIpSubnet || undefined,
      setGlobalIpSubnet,
      lastChanged: state.lastChanged || '',
      setLastChanged,
      globalSettings: state.globalSettings || undefined,
      setGlobalSettings,
      projectData: state.projectData || undefined,
      setProjectData,
      exportSettings: state.projectData.exportSettings || undefined,
      setExportSettings,
    }),
    [
      state.globalLoading,
      state.showDialogs,
      state.user,
      state.useCustomVlan,
      state.globalIpSubnet,
      state.lastChanged,
      state.globalSettings,
      state.projectData,
      setGlobalLoading,
      setShowDialogs,
      state.disableFlow,
      setDisableFlow,
      vlans,
      setUser,
      login,
      logout,
      setUseCustomVlan,
      setGlobalIpSubnet,
      setLastChanged,
      setGlobalSettings,
      setProjectData,
      setExportSettings,
    ],
  );

  return (
    <GlobalContext.Provider value={memoizedValue}>
      {children}
    </GlobalContext.Provider>
  );
}
